import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, Network, MailCheck, KeyRound, FileText,
  Zap, CheckCircle, ArrowRight, Menu, X, Terminal,
  Shield, CreditCard, Star, AlertTriangle, ChevronDown
} from 'lucide-react';
import { useState } from 'react';
import { openCookieConsent } from '../components/CookieConsent';

import TRANSLATIONS from '../data/landingContent';
import checkPages from '../data/checkPages';
import useSEO from '../hooks/useSEO';
import DomainStart from '../components/DomainStart';

const colorMap = {
  blue:   { text: 'text-blue-400',   border: 'border-blue-500/30',   bg: 'bg-blue-500/10',   btn: 'bg-blue-600 hover:bg-blue-700' },
  green:  { text: 'text-green-400',  border: 'border-green-500',     bg: 'bg-green-500/10',  btn: 'bg-green-600 hover:bg-green-700' },
  purple: { text: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/10', btn: 'bg-purple-600 hover:bg-purple-700' },
};

const featureIcons = [ShieldCheck, Network, MailCheck, KeyRound, FileText, Zap];

// Rapports publics : n'importe qui peut relancer le test et vérifier la note.
const TRUST_LINKS = {
  ssl: 'https://www.ssllabs.com/ssltest/analyze.html?d=cyber-guard.fr',
  observatory: 'https://developer.mozilla.org/en-US/observatory/analyze?host=cyber-guard.fr',
};

export default function LandingPage() {
  useSEO('/');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lang, setLang] = useState('fr');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const t = TRANSLATIONS[lang];
  const handleGetStarted = () => navigate(user ? '/dashboard' : '/register');

  return (
    <div className="min-h-screen bg-[#09090b] text-white">

      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-blue-500" />
            <span className="font-mono text-xl font-black text-white uppercase tracking-tighter">CyberGuard</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-zinc-400 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest">{t.nav.features}</a>
            <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest">{t.nav.pricing}</a>
            <a href="#faq" className="text-zinc-400 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest">{t.nav.faq}</a>
            <Link to="/blog" className="text-zinc-400 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest">{t.nav.blog}</Link>
            <Link to="/cgv" className="text-zinc-400 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest">{t.nav.cgv}</Link>
            {/* Sélecteur de langue */}
            <div className="flex items-center gap-1 border border-zinc-700 px-2 py-1 rounded-sm">
              {['fr', 'en', 'es'].map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`font-mono text-[10px] uppercase px-1 py-0.5 transition-colors ${lang === l ? 'text-blue-400 font-bold' : 'text-zinc-400 hover:text-white'}`}>
                  {l}
                </button>
              ))}
            </div>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 hover:bg-blue-700 font-mono rounded-none text-xs uppercase">{t.nav.dashboard}</Button>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-widest transition-colors">{t.nav.login}</Link>
                <Button onClick={() => navigate('/register')} className="bg-blue-600 hover:bg-blue-700 font-mono rounded-none text-xs uppercase">{t.nav.start}</Button>
              </div>
            )}
          </nav>
          <button className="md:hidden text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={mobileMenuOpen}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-zinc-900 border-t border-zinc-800 px-4 py-4 space-y-4">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-mono text-xs uppercase">{t.nav.features}</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-mono text-xs uppercase">{t.nav.pricing}</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-mono text-xs uppercase">{t.nav.faq}</a>
            <Link to="/blog" className="block text-zinc-400 hover:text-white font-mono text-xs uppercase">{t.nav.blog}</Link>
            <Link to="/cgv" className="block text-zinc-400 hover:text-white font-mono text-xs uppercase">{t.nav.cgv}</Link>
            <div className="flex items-center gap-2">
              {['fr', 'en', 'es'].map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`font-mono text-xs uppercase px-2 py-1 border rounded-sm transition-colors ${lang === l ? 'border-blue-500 text-blue-400' : 'border-zinc-700 text-zinc-400'}`}>
                  {l}
                </button>
              ))}
            </div>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="w-full bg-blue-600 font-mono rounded-none">{t.nav.dashboard}</Button>
            ) : (
              <>
                <Link to="/login" className="block text-zinc-400 hover:text-white font-mono text-xs uppercase">{t.nav.login}</Link>
                <Button onClick={() => navigate('/register')} className="w-full bg-blue-600 font-mono rounded-none">{t.nav.start}</Button>
              </>
            )}
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative pt-40 pb-24 px-4 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-blue-500/10 blur-[120px] pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 mb-8">
            <Terminal className="w-3 h-3 text-blue-400" />
            <span className="text-blue-400 text-xs font-mono uppercase tracking-widest">{t.badge}</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black font-mono uppercase tracking-tighter leading-none mb-6">
            {t.hero_title1}<br />
            <span className="text-blue-500">{t.hero_title2}</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-10 font-mono">
            {t.hero_sub} <span className="text-white font-bold">{t.hero_price}</span>.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={handleGetStarted} className="bg-blue-600 hover:bg-blue-700 px-8 py-6 text-base font-black font-mono uppercase rounded-none shadow-[0_0_30px_rgba(59,130,246,0.3)]">
              <ShieldCheck className="w-4 h-4 mr-2" /> {t.cta_scan} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800 px-8 py-6 text-base font-mono uppercase rounded-none" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
              {t.cta_learn}
            </Button>
          </div>
          <DomainStart lang={lang} />
          <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto mt-20 pt-10 border-t border-zinc-800">
            {[['50+', t.stat1], ['<2min', t.stat2], ['IA', t.stat3]].map(([val, label], i) => (
              <div key={i}>
                <div className="text-3xl font-black font-mono text-blue-400">{val}</div>
                <div className="text-zinc-400 text-xs font-mono uppercase tracking-widest mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEGAL WARNING */}
      <section className="px-4 py-6 border-t border-yellow-500/20 bg-yellow-500/5">
        <div className="max-w-5xl mx-auto flex items-start gap-4">
          <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-400 font-mono text-xs font-bold uppercase tracking-widest mb-1">{t.legal_title}</p>
            <p className="text-zinc-400 font-mono text-xs leading-relaxed">
              {t.legal_text} <span className="text-white font-bold">{t.legal_p1}</span> {t.legal_or} <span className="text-white font-bold">{t.legal_p2}</span>.
            </p>
            <a href="#faq" className="text-yellow-500 font-mono text-xs uppercase underline mt-2 inline-block hover:text-yellow-400">{t.legal_link}</a>
          </div>
        </div>
      </section>

      {/* NOUVELLES FONCTIONNALITES */}
      <section className="py-16 px-4 border-t border-zinc-800 bg-zinc-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 mb-4">
              <span className="text-green-400 text-[10px] font-mono uppercase tracking-widest">{t.new_badge}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black font-mono uppercase tracking-tighter text-white">{t.new_title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {t.new_features.map((f, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 hover:border-blue-500/30 p-6 transition-all">
                <div className="text-3xl mb-4">{f.icon}</div>
                <h3 className="text-white font-mono font-bold text-sm uppercase tracking-tight mb-2">{f.title}</h3>
                <p className="text-zinc-400 text-xs font-mono leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 px-4 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black font-mono uppercase tracking-tighter text-white mb-3">{t.features_title}</h2>
            <p className="text-zinc-400 font-mono text-sm max-w-xl mx-auto">{t.features_sub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.features.map((f, i) => {
              const Icon = featureIcons[i];
              return (
                <div key={i} className="bg-zinc-900 border border-zinc-800 p-6 hover:border-blue-500/40 transition-all group">
                  <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-all">
                    <Icon className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-white font-mono font-bold text-sm uppercase tracking-tight mb-2">{f.title}</h3>
                  <p className="text-zinc-400 text-xs font-mono leading-relaxed">{f.desc}</p>
                  {f.href && (
                    <Link to={f.href} className="inline-flex items-center gap-1 mt-3 text-blue-400 hover:text-blue-300 text-xs font-mono uppercase tracking-widest">
                      {f.linkLabel} <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PREUVE : NOTRE PROPRE SITE */}
      <section className="py-12 px-4 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto bg-zinc-900 border border-zinc-800 p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
          <ShieldCheck className="w-12 h-12 text-green-500 shrink-0" strokeWidth={1.5} />
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-white font-mono font-bold uppercase tracking-tight text-lg mb-1">{t.trust_title}</h2>
            <p className="text-zinc-400 text-sm">{t.trust_text}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a href={TRUST_LINKS.ssl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 border border-green-500/40 bg-green-500/10 hover:bg-green-500/20 px-4 py-2 font-mono text-xs uppercase text-green-400 transition-colors">
              <span className="font-black text-base">A+</span> {t.trust_ssl.replace(/^A\+ /, '')}
            </a>
            <a href={TRUST_LINKS.observatory} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 border border-green-500/40 bg-green-500/10 hover:bg-green-500/20 px-4 py-2 font-mono text-xs uppercase text-green-400 transition-colors">
              <span className="font-black text-base">A+</span> {t.trust_obs.replace(/^A\+ /, '')}
            </a>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 px-4 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black font-mono uppercase tracking-tighter text-white mb-3">{t.pricing_title}</h2>
            <p className="text-zinc-400 font-mono text-sm">{t.pricing_sub}</p>
            <Link to="/tarifs" className="inline-block mt-3 text-blue-400 hover:text-blue-300 font-mono text-xs uppercase tracking-widest">
              {lang === 'fr' ? 'Comparer les offres en détail →' : lang === 'en' ? 'Compare plans in detail (in French) →' : 'Comparar las ofertas en detalle (en francés) →'}
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {t.plans.map((plan) => {
              const c = colorMap[plan.color];
              const icons = { blue: Shield, green: Zap, purple: CreditCard };
              const Icon = icons[plan.color];
              return (
                <div key={plan.name} className={`relative bg-zinc-900 border rounded-sm p-8 ${plan.popular ? 'border-green-500 shadow-[0_0_40px_rgba(34,197,94,0.1)]' : 'border-zinc-800'}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-green-500 text-black text-[10px] font-black font-mono px-3 py-1 uppercase tracking-widest flex items-center gap-1">
                        <Star className="w-3 h-3" /> {t.recommended}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center border ${c.border} ${c.bg}`}>
                      <Icon className={`w-5 h-5 ${c.text}`} />
                    </div>
                    <h3 className="text-lg font-black text-white font-mono uppercase">{plan.name}</h3>
                  </div>
                  <div className="mb-6">
                    <span className={`text-5xl font-black font-mono ${c.text}`}>{plan.price}€</span>
                    <span className="text-zinc-400 font-mono text-sm">{t.per_month}</span>
                  </div>
                  <ul className="space-y-2 mb-8">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs font-mono text-zinc-400">
                        <CheckCircle className={`w-4 h-4 shrink-0 mt-0.5 ${c.text}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={handleGetStarted} className={`w-full font-mono rounded-none text-xs font-bold uppercase ${c.btn}`}>
                    {plan.cta} <ArrowRight className="w-3 h-3 ml-2" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-4 border-t border-zinc-800 bg-zinc-900/20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black font-mono uppercase tracking-tighter text-white mb-3">{t.faq_title}</h2>
            <p className="text-zinc-400 font-mono text-sm">{t.faq_sub}</p>
          </div>
          <div className="space-y-2">
            {t.faqs.map((faq, i) => (
              <div key={i} className={`border rounded-sm transition-all ${openFaq === i ? 'border-blue-500/40 bg-zinc-900' : 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700'}`}>
                <button className="w-full flex items-center justify-between p-5 text-left" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="text-white font-mono font-bold text-sm pr-4">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180 text-blue-400' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <div className="border-t border-zinc-800 pt-4">
                      <p className="text-zinc-400 font-mono text-xs leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-zinc-400 font-mono text-xs">
              {lang === 'fr' ? 'Une autre question ?' : lang === 'en' ? 'Another question?' : '¿Otra pregunta?'}{' '}
              <a href="mailto:contact@cyber-guard.fr" className="text-blue-400 underline hover:text-blue-300">contact@cyber-guard.fr</a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-blue-500/5 border border-blue-500/20 p-16 relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-blue-500/10 blur-[80px] pointer-events-none" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-black font-mono uppercase tracking-tighter text-white mb-4">{t.cta_title}</h2>
              <p className="text-zinc-400 font-mono text-sm mb-8">{t.cta_sub}</p>
              <Button onClick={handleGetStarted} className="bg-blue-600 hover:bg-blue-700 px-10 py-6 text-base font-black font-mono uppercase rounded-none shadow-[0_0_30px_rgba(59,130,246,0.3)]">
                <ShieldCheck className="w-4 h-4 mr-2" /> {t.cta_btn} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-800 py-10 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-500" />
            <span className="font-mono text-lg font-black text-white uppercase tracking-tighter">CyberGuard</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/blog" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors">{t.nav.blog}</Link>
            {checkPages.map((c) => (
              <Link key={c.slug} to={`/${c.slug}`} className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors">{c.navLabel}</Link>
            ))}
            <Link to="/tarifs" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors">{t.nav.pricing}</Link>
            <Link to="/cyberguard-et-monaidecyber" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors">MonAideCyber</Link>
            <Link to="/cgv" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors">CGU</Link>
            <Link to="/mentions-legales" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors">{t.footer_legal}</Link>
            <Link to="/confidentialite" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors">{t.footer_privacy}</Link>
            <button onClick={openCookieConsent} className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors">{t.footer_cookies}</button>
            <a href={TRUST_LINKS.ssl} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 text-xs font-mono uppercase tracking-widest transition-colors">{t.trust_ssl}</a>
            <a href={TRUST_LINKS.observatory} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 text-xs font-mono uppercase tracking-widest transition-colors">{t.trust_obs}</a>
            <span className="text-zinc-400 text-xs font-mono uppercase tracking-widest">{t.footer_rights}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
